/**
 * Support Tickets Page - Instance View
 * Users can create, view and manage their support tickets
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Modal } from '../../components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  TicketIcon,
  Plus,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  ChevronRight,
  User,
  Headphones,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PRIORITY_LABELS = {
  low: { label: 'Baixa', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  critical: { label: 'Crítica', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const STATUS_LABELS = {
  open: { label: 'Aberto', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: AlertCircle },
  in_progress: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
  waiting_response: { label: 'Aguardando Resposta', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  resolved: { label: 'Resolvido', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: CheckCircle },
};

const CATEGORY_LABELS = {
  technical: 'Técnico',
  financial: 'Financeiro',
  general: 'Geral',
  bug: 'Bug/Erro',
  feature_request: 'Sugestão',
};

export default function Tickets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // New ticket modal
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });
  const [creating, setCreating] = useState(false);
  
  // Filter
  const [statusFilter, setStatusFilter] = useState('all');

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const [ticketsRes, statsRes] = await Promise.all([
        api.get('/tickets'),
        api.get('/tickets/stats/summary'),
      ]);
      setTickets(ticketsRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do ticket');
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      toast.error('Preencha título e descrição');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/tickets', newTicket);
      toast.success('Ticket criado com sucesso');
      setNewTicketOpen(false);
      setNewTicket({ title: '', description: '', category: 'general', priority: 'medium' });
      await loadTickets();
      
      // Open the new ticket
      if (response.data?.data?.id) {
        await loadTicketDetails(response.data.data.id);
      }
    } catch (error) {
      toast.error('Erro ao criar ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    setSendingMessage(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/messages`, { content: newMessage });
      setNewMessage('');
      await loadTicketDetails(selectedTicket.id);
      await loadTickets();
      toast.success('Mensagem enviada');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;

    try {
      await api.post(`/tickets/${selectedTicket.id}/close`);
      toast.success('Ticket fechado');
      await loadTickets();
      await loadTicketDetails(selectedTicket.id);
    } catch (error) {
      toast.error('Erro ao fechar ticket');
    }
  };

  const filteredTickets = useMemo(() => {
    if (statusFilter === 'all') return tickets;
    return tickets.filter(t => t.status === statusFilter);
  }, [tickets, statusFilter]);

  const formatDate = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <PageHeader
        title="Suporte"
        icon={TicketIcon}
        subtitle={`${stats.total} ticket(s) - ${stats.open} aberto(s)`}
        actions={
          <Button onClick={() => setNewTicketOpen(true)} data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Ticket
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-primary" onClick={() => setStatusFilter('open')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Abertos</p>
                <p className="text-2xl font-bold text-green-600">{stats.open || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-green-600/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Em Andamento</p>
                <p className="text-2xl font-bold text-blue-600">{stats.in_progress || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setStatusFilter('waiting_response')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aguardando</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.waiting_response || 0}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-yellow-600/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary" onClick={() => setStatusFilter('all')}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolvidos</p>
                <p className="text-2xl font-bold text-emerald-600">{(stats.resolved || 0) + (stats.closed || 0)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-600/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Tickets</CardTitle>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="open">Abertos</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="waiting_response">Aguardando</SelectItem>
                    <SelectItem value="resolved">Resolvidos</SelectItem>
                    <SelectItem value="closed">Fechados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {filteredTickets.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <TicketIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhum ticket encontrado</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredTickets.map(ticket => {
                      const status = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;
                      const priority = PRIORITY_LABELS[ticket.priority] || PRIORITY_LABELS.medium;
                      const StatusIcon = status.icon;
                      
                      return (
                        <button
                          key={ticket.id}
                          onClick={() => loadTicketDetails(ticket.id)}
                          className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                            selectedTicket?.id === ticket.id ? 'bg-muted' : ''
                          }`}
                          data-testid={`ticket-${ticket.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{ticket.title}</p>
                              <p className="text-sm text-muted-foreground truncate mt-1">
                                {ticket.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={priority.color} variant="secondary">
                                  {priority.label}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(ticket.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <StatusIcon className={`h-4 w-4 ${
                                ticket.status === 'open' ? 'text-green-600' :
                                ticket.status === 'in_progress' ? 'text-blue-600' :
                                ticket.status === 'waiting_response' ? 'text-yellow-600' :
                                'text-gray-400'
                              }`} />
                              {ticket.messages?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {ticket.messages.length}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Ticket Details */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle>{selectedTicket.title}</CardTitle>
                    <CardDescription className="mt-2">
                      {selectedTicket.description}
                    </CardDescription>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge className={STATUS_LABELS[selectedTicket.status]?.color}>
                        {STATUS_LABELS[selectedTicket.status]?.label}
                      </Badge>
                      <Badge className={PRIORITY_LABELS[selectedTicket.priority]?.color} variant="secondary">
                        {PRIORITY_LABELS[selectedTicket.priority]?.label}
                      </Badge>
                      <Badge variant="outline">
                        {CATEGORY_LABELS[selectedTicket.category]}
                      </Badge>
                    </div>
                  </div>
                  {selectedTicket.status !== 'closed' && (
                    <Button variant="outline" size="sm" onClick={handleCloseTicket}>
                      Fechar Ticket
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Messages */}
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-4">
                    {selectedTicket.messages?.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p>Nenhuma mensagem ainda</p>
                      </div>
                    ) : (
                      selectedTicket.messages?.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex gap-3 ${
                            msg.user_role === 'admin-master' ? 'flex-row-reverse' : ''
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            msg.user_role === 'admin-master' 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted'
                          }`}>
                            {msg.user_role === 'admin-master' ? (
                              <Headphones className="h-4 w-4" />
                            ) : (
                              <User className="h-4 w-4" />
                            )}
                          </div>
                          <div className={`flex-1 max-w-[80%] ${
                            msg.user_role === 'admin-master' ? 'text-right' : ''
                          }`}>
                            <div className={`rounded-lg p-3 ${
                              msg.user_role === 'admin-master'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {msg.user_name} • {formatDate(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {/* Reply Box */}
                {selectedTicket.status !== 'closed' && (
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      data-testid="message-input"
                    />
                    <Button onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-[500px] text-muted-foreground">
                <MessageSquare className="h-16 w-16 mb-4 opacity-30" />
                <p className="text-lg">Selecione um ticket para ver os detalhes</p>
                <p className="text-sm mt-1">ou crie um novo ticket de suporte</p>
                <Button className="mt-4" onClick={() => setNewTicketOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Ticket
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      <Modal
        open={newTicketOpen}
        onOpenChange={setNewTicketOpen}
        title="Novo Ticket de Suporte"
        description="Descreva seu problema ou dúvida e nossa equipe responderá o mais breve possível."
        size="default"
        onConfirm={handleCreateTicket}
        onCancel={() => setNewTicketOpen(false)}
        confirmText={creating ? 'Criando...' : 'Criar Ticket'}
        cancelText="Cancelar"
        loading={creating}
        disableConfirm={!newTicket.title || !newTicket.description}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              value={newTicket.title}
              onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Resumo do problema..."
              data-testid="ticket-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição *</Label>
            <Textarea
              id="description"
              value={newTicket.description}
              onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descreva detalhadamente o problema ou dúvida..."
              rows={4}
              data-testid="ticket-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={newTicket.category}
                onValueChange={(v) => setNewTicket(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Geral</SelectItem>
                  <SelectItem value="technical">Técnico</SelectItem>
                  <SelectItem value="financial">Financeiro</SelectItem>
                  <SelectItem value="bug">Bug/Erro</SelectItem>
                  <SelectItem value="feature_request">Sugestão</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select
                value={newTicket.priority}
                onValueChange={(v) => setNewTicket(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baixa</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="critical">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
